import { Info } from 'lucide-react';
import type { Item } from '@/lib/schema/types';
import { useObservation } from '@/lib/state/useObservation';

/**
 * Per-item "Not Applicable" toggle, rendered only for items whose schema
 * entry sets `item_allows_na: true` — items 16, 18, 22, 30, 31 and 33, per the
 * Scoresheet's item score boxes.
 *
 * The banner states the rubric's own condition for that item ("Score this item
 * NA if only one child is enrolled…") rather than a generic line, so an
 * observer can tell whether NA applies without leaving the page.
 *
 * When active:
 *   - The indicator grid dims and becomes non-interactive (handled upstream).
 *   - The scoring engine returns `{ score: null, itemNa: true, complete: true }`.
 *   - Complete becomes available without requiring a full sweep.
 */
export function ItemNaBanner({ item }: { item: Item }) {
  const { state, setItemNa } = useObservation();
  if (!item.item_allows_na) return null;
  const active = state.itemNa[item.id] ?? false;

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-b bg-muted/40 px-6 py-2 text-sm"
      aria-live="polite"
    >
      <Info className="h-4 w-4 flex-shrink-0 text-dece-sky" aria-hidden />
      <span className="flex-1 text-foreground/80">
        {item.item_na_condition?.trim() ? (
          item.item_na_condition
        ) : (
          <>
            This item can be marked <strong>Not Applicable</strong> when the
            provider&apos;s program does not cover it.
          </>
        )}
      </span>
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-1.5">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setItemNa(item.id, e.target.checked)}
          className="h-4 w-4"
          aria-label={`Mark ${item.name} as Not Applicable`}
        />
        <span className="font-medium">Item is Not Applicable</span>
      </label>
    </div>
  );
}
